import { fetchCustomerSupportResponse } from "@/services/schaden-ki/fetch-customer-support-response";
import { ChatStore } from "@/stores/ChatStore";
import SettingsStore from "@/stores/SettingsStore";
import type { ResponseInputAudioTranscriptionCompleted } from "@/types";
import { featureFlags } from "@/lib/featureFlags";
import { bbLog, bbWarn, previewText } from "@/lib/blockbrainTrace";

const BLOCKBRAIN_TRIGGER_REGEX = /\bblock\s*brain\b/i;
// User explicitly leaves the Blockbrain context and wants to return to SchadenKI / claim handling
const BLOCKBRAIN_EXIT_REGEX = /\b(zur(ü|ue)ck zur schadenmeldung|schadenmeldung starten|stop blockbrain|exit blockbrain|blockbrain beenden|raus aus blockbrain)\b/i;

export default function onReceivedInputAudioTranscriptionCompleted(message: ResponseInputAudioTranscriptionCompleted) {
  console.log("Transcript:", message);

  // The transcript is already a plain string, no need to parse it
  let processedTranscript = message.transcript;

  // this is a workaround for the fact that gpt-4o-transcribe does not add a period at the end of the transcript
  if (processedTranscript?.[processedTranscript.length - 1]?.match(/[a-zA-Z0-9]/)) {
    processedTranscript += ".";
  }

  ChatStore.getState().updateLastMessageTranscript(processedTranscript, "user");

  const useSchadenKI = SettingsStore.getState().useSchadenKI;
  const blockbrainEnabled = featureFlags.blockbrain;
  const text = processedTranscript || "";
  const hasTrigger = blockbrainEnabled && BLOCKBRAIN_TRIGGER_REGEX.test(text);
  const wantsExit = blockbrainEnabled && BLOCKBRAIN_EXIT_REGEX.test(text);
  let inBlockbrain = ChatStore.getState().inBlockbrainConversation;

  bbLog("voice.transcript", {
    blockbrainEnabled,
    useSchadenKI,
    hasTrigger,
    wantsExit,
    stickyBefore: inBlockbrain,
    waiting: ChatStore.getState().isWaitingForBlockbrain,
    preview: previewText(text),
  });

  if (wantsExit && inBlockbrain) {
    bbLog("voice.exitSticky");
    ChatStore.getState().setInBlockbrainConversation(false);
    inBlockbrain = false;
  } else if (hasTrigger && !inBlockbrain) {
    bbLog("voice.enterSticky");
    ChatStore.getState().setInBlockbrainConversation(true);
    inBlockbrain = true;
  }

  if (blockbrainEnabled && (hasTrigger || inBlockbrain)) {
    // Hand the user's already-transcribed turn to the Realtime API so the LLM
    // can call the ask_blockbrain tool, regardless of SchadenKI being active.
    bbLog("voice.routeToRealtime", { hasTrigger, sticky: inBlockbrain, useSchadenKI });
    ChatStore.getState().setAvatarStatus("Frage Blockbrain");
    ChatStore.getState().setIsWaitingForBlockbrain(true);
    // Safety net: clear the spinner after 180 s.
    window.setTimeout(() => {
      if (ChatStore.getState().isWaitingForBlockbrain) {
        bbWarn("voice.timeout180s.clearWaiting");
        ChatStore.getState().setIsWaitingForBlockbrain(false);
        if (ChatStore.getState().avatarStatus === "Frage Blockbrain") {
          ChatStore.getState().setAvatarStatus("Bereit");
        }
      }
    }, 180_000);

    // Only manually trigger response.create when SchadenKI disabled VAD auto-response.
    // Otherwise VAD already creates a response and a second trigger races.
    if (useSchadenKI) {
      const trigger = ChatStore.getState().triggerRealtimeResponse;
      if (trigger) {
        bbLog("voice.triggerResponse.create");
        trigger();
        return;
      }
      bbWarn("voice.triggerResponse.missing");
    } else {
      bbLog("voice.kbMode.awaitVadAutoResponse");
      // Knowledge-base mode: create_response=true already handles the turn.
      return;
    }
  }

  if (useSchadenKI) {
    ChatStore.getState().setAvatarStatus("Denken");
    fetchCustomerSupportResponse(processedTranscript);
  }
}
